import { inject, Injectable, Provider } from "@angular/core";
import { Observable, Subscription } from "rxjs";
import { IReminderApiService, IReminderApiServiceToken } from "./reminder-api-service.interface";
import {
  IReminderListGetQueryResult, ReminderClient, ReminderDeleteCommand, ReminderDeleteCommandResult,
  ReminderDto, ReminderInitializeCommand, ReminderInitializeCommandResult, ReminderListGetQuery,
  ReminderSetCommand, ReminderSetCommandResult
} from "../../INTEGRA_Shop_Client";

@Injectable({
  providedIn: 'root'
})
export class ReminderApiService implements IReminderApiService {
  private readonly reminderClient: ReminderClient = inject(ReminderClient);
  private reminderListGetSubscription: Subscription | undefined;

  public reminderListGet(
    params?: Partial<ReminderListGetQuery>,
    allowCancellation?: boolean | undefined
  ): Observable<IReminderListGetQueryResult> {
    let query = new ReminderListGetQuery({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      customerNumber: params?.customerNumber,
    });

    // no cancellation: normal request
    if (!allowCancellation) {
      return this.reminderClient.reminderListGet(query);
    }

    // cancellation: kill old subscription
    if (this.reminderListGetSubscription) {
      this.reminderListGetSubscription.unsubscribe();
      this.reminderListGetSubscription = undefined;
    }

    // new observable with manuel subscription management
    return new Observable<IReminderListGetQueryResult>(observer => {
      this.reminderListGetSubscription = this.reminderClient.reminderListGet(query).subscribe({
        next: (result) => observer.next(result),
        error: (error) => {
          observer.error(error);
          this.reminderListGetSubscription = undefined;
        },
        complete: () => {
          observer.complete();
          this.reminderListGetSubscription = undefined;
        }
      });

      // cleanup upon component unsubscribed
      return () => {
        if (this.reminderListGetSubscription) {
          this.reminderListGetSubscription.unsubscribe();
          this.reminderListGetSubscription = undefined;
        }
      };
    });
  }

  public reminderSet(reminder?: ReminderDto | undefined): Observable<ReminderSetCommandResult> {
    let command = new ReminderSetCommand({
      reminder: reminder
    });
    return this.reminderClient.reminderSet(command);
  }

  public reminderInitialize(reminder?: ReminderDto | undefined): Observable<ReminderInitializeCommandResult> {
    let command = new ReminderInitializeCommand({
      reminder: reminder
    });
    return this.reminderClient.reminderInitialize(command);
  }

  public reminderDelete(userNumber: string): Observable<ReminderDeleteCommandResult> {
    let command = new ReminderDeleteCommand({
      userNumber: userNumber
    });

    return this.reminderClient.reminderDelete(command);
  }
}

/**
 * Prepared provider with the token to implementation mapping.
 * Reference this at the desired provider list to enable the injection of this implementation by referencing the token(interface).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const ReminderApiServiceProvider: Provider[] = [
  { provide: ReminderClient },
  { provide: IReminderApiServiceToken, useExisting: ReminderApiService }
];
